import React, { useCallback, useState, createContext, useContext } from 'react';
import { Toast, ToastType } from '../components/Toast/Toast';
interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number, actionLabel?: string, onAction?: () => void) => void;
  hideToast: () => void;
  showEmailToast: (status: 'pending' | 'success' | 'error', message?: string, onRetry?: () => void) => void;
  showScanToast: (status: 'complete' | 'failed', message: string, duration?: number) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    duration: number;
    isVisible: boolean;
    actionLabel?: string;
    onAction?: () => void;
  }>({
    message: '',
    type: 'success',
    duration: 3000,
    isVisible: false
  });
  const showToast = useCallback((message: string, type: ToastType = 'success', duration: number = 3000, actionLabel?: string, onAction?: () => void) => {
    setToast({
      message,
      type,
      duration,
      isVisible: true,
      actionLabel,
      onAction
    });
  }, []);
  const hideToast = useCallback(() => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);
  const showEmailToast = useCallback((status: 'pending' | 'success' | 'error', message?: string, onRetry?: () => void) => {
    if (status === 'pending') {
      showToast(message || 'Sending email...', 'pending', 0); // 0 duration means it won't auto-dismiss
    } else if (status === 'success') {
      showToast(message || 'Email sent successfully!', 'email-success', 3000);
    } else if (status === 'error') {
      showToast(message || 'Failed to send email', 'email-error', 5000, onRetry ? 'Retry' : undefined, onRetry);
    }
  }, [showToast]);
  const showScanToast = useCallback((status: 'complete' | 'failed', message: string, duration: number = 5000) => {
    if (status === 'complete') {
      showToast(message, 'scan-complete', duration);
    } else {
      showToast(message, 'scan-failed', duration);
    }
  }, [showToast]);
  return <ToastContext.Provider value={{
    showToast,
    hideToast,
    showEmailToast,
    showScanToast
  }} data-id="element-3038" data-mp="2xg7g91n785tj">
      {children}
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} duration={toast.duration} actionLabel={toast.actionLabel} onAction={toast.onAction} data-id="element-3039" data-mp="37722w74cx7fi" />
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}